// components/AvatarCropDialog.tsx
"use client";

import { useState, useCallback, useRef } from "react";
import { Camera, FileUp, FolderOpen } from "lucide-react";
import { Point, type Area } from "react-easy-crop";
import {
  AvatarComponent,
  Dropzone,
  CropperUI,
  AvatarCropDialogProps,
  validateAvatarFile,
  processAvatarCrop,
  useUploadThing,
  AVATAR_CONFIG,
} from "@US";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  DialogTrigger,
  DialogClose,
} from "visuais";
import { useUser, useAvatarStore } from "store";
import { toast } from "sonner";

export function AvatarCropDialog({
  username,
  onConfirm,
}: AvatarCropDialogProps & { username: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { startUpload } = useUploadThing("profilePictureUploader", {
    onClientUploadComplete: (res) => {
      console.log("Upload completo:", res);
    },
    onUploadError: (error) => {
      console.log("Erro no upload:", error);
      alert(`Erro no upload: ${error.message}`);
    },
  });

  const handleDialogClose = useCallback((open: boolean) => {
    if (!open) {
      setSelectedFile(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setIsUploading(false);
    }
    setIsOpen(open);
  }, []);

  const handleCropChange = useCallback((point: Point) => {
    setCrop(point);
  }, []);

  const handleZoomChange = useCallback((zoom: number) => {
    setZoom(zoom);
  }, []);

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedPixels: Area) => {
      setCroppedAreaPixels(croppedPixels);
    },
    []
  );

  const handleFileSelect = useCallback((file: File) => {
    const validation = validateAvatarFile(file);

    if (!validation.isValid) {
      alert(validation.error);
      return;
    }

    setSelectedFile(file);
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
      event.target.value = "";
    },
    [handleFileSelect]
  );

  const handleConfirmCrop = async () => {
    if (!croppedAreaPixels || !selectedFile) return;
    setIsUploading(true);
    try {
      const processedFile = await processAvatarCrop(
        selectedFile,
        croppedAreaPixels
      );
      const uploadResult = await startUpload([processedFile]);
      if (uploadResult && uploadResult.length > 0) {
        const fileUrl = uploadResult[0].url;
        // Atualiza o cache local global
        if (username && fileUrl) {
          useAvatarStore.getState().setAvatar(username, fileUrl);
        }
        toast.success("Avatar atualizado com sucesso!");
        onConfirm(fileUrl);
        handleDialogClose(false);
      } else {
        console.log(AVATAR_CONFIG.ERRORS.UPLOAD_FAILED);
      }
    } catch (error) {
      console.log("Erro durante o processo:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : AVATAR_CONFIG.ERRORS.UNKNOWN_ERROR
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogClose}>
      <DialogTrigger asChild>
        <div className="relative cursor-pointer group">
          <AvatarComponent className="w-24 h-24" username={username} />
          <div className="absolute bg-gray-200 text-black bottom-0 right-0 rounded-full w-8 h-8 p-0 flex justify-center items-center group-hover:bg-gray-300 transition-colors">
            <Camera className="size-5" />
          </div>
        </div>
      </DialogTrigger>

      <DialogContent className="max-w-2xl w-full">
        <DialogHeader>
          <DialogTitle>
            {selectedFile
              ? AVATAR_CONFIG.UI_TEXTS.DIALOG_TITLE_EDIT
              : AVATAR_CONFIG.UI_TEXTS.DIALOG_TITLE_UPLOAD}
          </DialogTitle>
          {!selectedFile && (
            <DialogDescription>
              {AVATAR_CONFIG.UI_TEXTS.DIALOG_DESCRIPTION}
            </DialogDescription>
          )}
        </DialogHeader>

        {!selectedFile ? (
          <Dropzone
            onDrop={handleFileSelect}
            onClick={() => fileInputRef.current?.click()}
            className="flex h-48 w-full cursor-pointer flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed border-muted-foreground/30 p-6 text-center text-muted-foreground transition-colors hover:bg-muted/50"
          >
            <FileUp className="h-10 w-10" />
            <p className="font-semibold">
              {AVATAR_CONFIG.UI_TEXTS.DROPZONE_MAIN}
            </p>
            <p className="text-sm">
              {AVATAR_CONFIG.UI_TEXTS.DROPZONE_SECONDARY.split(" clique ")[0]}
              <span className="ml-1 inline-flex items-center font-semibold text-primary">
                <FolderOpen className="mr-1 h-4 w-4" />
                clique para navegar
              </span>
            </p>
            <input
              type="file"
              accept={AVATAR_CONFIG.ACCEPTED_FILE_TYPES.join(",")}
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
            />
          </Dropzone>
        ) : (
          <>
            <div className="relative h-full w-full">
              <CropperUI
                image={URL.createObjectURL(selectedFile)}
                crop={crop}
                zoom={zoom}
                onCropChange={handleCropChange}
                onZoomChange={handleZoomChange}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <DialogClose asChild>
                <Button variant="outline" size="sm" disabled={isUploading}>
                  {AVATAR_CONFIG.UI_TEXTS.BUTTON_CANCEL}
                </Button>
              </DialogClose>
              <Button
                onClick={handleConfirmCrop}
                size="sm"
                disabled={isUploading || !croppedAreaPixels}
              >
                {isUploading
                  ? AVATAR_CONFIG.UI_TEXTS.BUTTON_UPLOADING
                  : AVATAR_CONFIG.UI_TEXTS.BUTTON_USE_IMAGE}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
